import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ExternalLink, Printer } from "lucide-react";
import Chapter, { FadeIn } from "./Chapter";
import { SPOTS, PHOTOS, mapsUrl } from "../data/spots";

const EASE = [0.16, 1, 0.3, 1];

const FILTERS = [
  { id: "all", label: "All 17" },
  { id: "approved", label: "Pranjal Approved" },
  { id: "late-night", label: "Late-Night" },
  { id: "truck", label: "Food Trucks" },
  { id: "perfect-10", label: "Perfect 10s" },
  { id: "gap", label: "The Gap" },
];

const SORTS = [
  { id: "rank", label: "Rank" },
  { id: "salsa", label: "Salsa" },
  { id: "tortilla", label: "Tortilla" },
];

const Bar = ({ label, value, accent = false, tid }) => (
  <div>
    <div className="flex justify-between font-mono text-[10px] tracking-[0.14em] uppercase mb-1">
      <span className="text-ink2">{label}</span>
      <span className={accent ? "text-salsa font-bold" : "font-bold"} data-testid={tid}>
        {value == null ? "—" : value.toFixed(1)}
      </span>
    </div>
    <div className="score-track">
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: value == null ? 0 : value / 10 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease: EASE }}
        className={`score-fill ${accent ? "accent" : ""}`}
      />
    </div>
  </div>
);

const SpotRow = ({ spot, open, onToggle }) => (
  <div className="border-b border-ink/20" data-testid={`spot-row-${String(spot.rank).padStart(2, "0")}`}>
    <button
      data-testid={`spot-toggle-${spot.id}`}
      onClick={onToggle}
      className={`w-full text-left grid grid-cols-12 items-center gap-2 sm:gap-4 px-2 sm:px-4 py-5 transition-colors duration-150 ${
        open ? "bg-paper2" : "hover:bg-paper2"
      }`}
    >
      <span className="col-span-2 sm:col-span-1 font-mono text-xs sm:text-sm text-muted2">
        {String(spot.rank).padStart(2, "0")}
      </span>
      <span className="col-span-8 sm:col-span-5 min-w-0">
        <span className="block font-serif font-semibold text-xl sm:text-3xl leading-tight truncate">
          {spot.name}
        </span>
        <span className="block mt-1 font-mono text-[9px] sm:text-[10px] tracking-[0.14em] uppercase text-muted2 truncate">
          {spot.type} — {spot.hood}
        </span>
        {spot.approved ? (
          <span className="stamp text-[8px] mt-2 hidden sm:inline-block">Pranjal Approved</span>
        ) : (
          <span className="mt-2 hidden sm:inline-block font-mono text-[8px] tracking-[0.14em] uppercase border border-ink/40 text-ink/50 px-1.5 py-0.5 rotate-[-2deg]">
            × {spot.verdict}
          </span>
        )}
      </span>
      <span className="hidden sm:block sm:col-span-2 font-mono text-xs">
        <span className="text-muted2 text-[9px] tracking-[0.14em] uppercase block">Salsa</span>
        {spot.scores.salsa == null ? "—" : spot.scores.salsa.toFixed(1)}
      </span>
      <span className="hidden sm:block sm:col-span-2 font-mono text-xs">
        <span className="text-muted2 text-[9px] tracking-[0.14em] uppercase block">Tortilla</span>
        {spot.scores.tortilla == null ? "—" : spot.scores.tortilla.toFixed(1)}
      </span>
      <span className="col-span-2 sm:col-span-1 text-right font-mono font-bold text-lg sm:text-2xl">
        <span className={spot.approved ? "text-salsa" : "text-ink/50"} data-testid={`spot-score-${spot.id}`}>
          {spot.score.toFixed(spot.score % 1 === 0 ? 1 : 2)}
        </span>
      </span>
      <span className="hidden sm:flex sm:col-span-1 justify-end">
        <ChevronDown size={16} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </span>
    </button>

    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="overflow-hidden bg-paper2"
        >
          <div className="grid md:grid-cols-12 gap-8 px-4 sm:px-8 py-8 border-t border-ink/10">
            <div className="md:col-span-4">
              <div className="border-2 border-ink shadow-[6px_6px_0_rgba(26,26,26,0.9)]">
                <img
                  src={PHOTOS[spot.id]}
                  alt={`${spot.name} — the spot itself`}
                  loading="lazy"
                  className="photo-treatment w-full aspect-[4/3] object-cover"
                  data-testid={`spot-photo-${spot.id}`}
                />
              </div>
              <p className="mt-2 font-mono text-[9px] tracking-[0.16em] uppercase text-muted2">
                {spot.aka} · The spot itself, verified photo
              </p>
            </div>
            <div className="md:col-span-5 space-y-5">
              <p className="font-body text-sm md:text-base leading-relaxed text-ink2">{spot.blurb}</p>
              <div className="border border-dashed border-ink/50 p-4">
                <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-salsa mb-1">Field Note</p>
                <p className="font-mono text-xs leading-relaxed">{spot.quirk}</p>
              </div>
              <p className="font-mono text-[10px] tracking-[0.12em] uppercase text-ink2" data-testid={`spot-online-${spot.id}`}>
                Online — {spot.online}
              </p>
            </div>
            <div className="md:col-span-3 space-y-4">
              <Bar label="Al Pastor" value={spot.scores.pastor} tid={`bar-pastor-${spot.id}`} />
              <Bar label="Carne Asada" value={spot.scores.asada} tid={`bar-asada-${spot.id}`} />
              <Bar label="Chicken" value={spot.scores.chicken} tid={`bar-chicken-${spot.id}`} />
              <Bar label="Salsa" value={spot.scores.salsa} accent tid={`bar-salsa-${spot.id}`} />
              <Bar label="Tortilla" value={spot.scores.tortilla} accent tid={`bar-tortilla-${spot.id}`} />
              {spot.specialty && (
                <Bar label={spot.specialty.name} value={spot.specialty.score} accent tid={`bar-specialty-${spot.id}`} />
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 px-4 sm:px-8 py-5 border-t border-ink/10">
            <div className="font-mono text-[10px] sm:text-xs tracking-[0.08em] text-ink2">
              <span className="uppercase">{spot.address}</span>
              <span className="text-muted2"> · {spot.hours} · {spot.price}</span>
            </div>
            <a
              data-testid={`maps-link-${spot.id}`}
              href={mapsUrl(spot)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-ink px-4 py-2 font-mono text-[10px] tracking-[0.16em] uppercase hover:bg-ink hover:text-paper transition-colors"
            >
              Open in Google Maps <ExternalLink size={12} />
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const TacoIndex = () => {
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("rank");
  const [openId, setOpenId] = useState(null);
  const [printAll, setPrintAll] = useState(false);

  const spots = useMemo(() => {
    let list = [...SPOTS];
    if (filter === "approved") list = list.filter((s) => s.approved);
    if (filter === "late-night") list = list.filter((s) => s.tags.includes("late-night"));
    if (filter === "truck") list = list.filter((s) => s.tags.includes("truck"));
    if (filter === "perfect-10") list = list.filter((s) => s.tags.includes("perfect-10"));
    if (filter === "gap") list = list.filter((s) => !s.approved);
    if (sort === "salsa") list.sort((a, b) => (b.scores.salsa ?? -1) - (a.scores.salsa ?? -1));
    else if (sort === "tortilla") list.sort((a, b) => (b.scores.tortilla ?? -1) - (a.scores.tortilla ?? -1));
    else list.sort((a, b) => a.rank - b.rank);
    return list;
  }, [filter, sort]);

  const handlePrint = () => {
    setPrintAll(true);
    setTimeout(() => {
      window.print();
      setPrintAll(false);
    }, 600);
  };

  return (
    <>
      <Chapter no="03" kicker="The Ratings" title="The Index — 17 Spots, Ranked" id="the-index" />
      <section className="px-4 sm:px-8 pb-28" data-testid="taco-index">
        <FadeIn className="no-print flex flex-wrap items-end justify-between gap-6 mb-6">
          <div>
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted2 mb-2">Filter</p>
            <div className="flex flex-wrap gap-2" data-testid="filter-group">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  data-testid={`filter-${f.id}`}
                  data-active={filter === f.id}
                  onClick={() => setFilter(f.id)}
                  className="border border-ink px-3 py-1.5 font-mono text-[10px] tracking-[0.14em] uppercase transition-colors hover:bg-ink hover:text-paper data-[active=true]:bg-ink data-[active=true]:text-paper"
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted2 mb-2">Sort</p>
            <div className="flex gap-2" data-testid="sort-group">
              {SORTS.map((s) => (
                <button
                  key={s.id}
                  data-testid={`sort-${s.id}`}
                  data-active={sort === s.id}
                  onClick={() => setSort(s.id)}
                  className="border border-ink px-3 py-1.5 font-mono text-[10px] tracking-[0.14em] uppercase transition-colors hover:bg-ink hover:text-paper data-[active=true]:bg-ink data-[active=true]:text-paper"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </FadeIn>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted2" data-testid="index-count">
            Showing {spots.length} of {SPOTS.length} · Composite score / 10
          </p>
          <button
            data-testid="print-report-btn"
            onClick={handlePrint}
            className="no-print inline-flex items-center gap-2 border border-ink px-4 py-2 font-mono text-[10px] tracking-[0.16em] uppercase hover:bg-ink hover:text-paper transition-colors"
          >
            <Printer size={12} /> Print Field Report
          </button>
        </div>

        <div className="border-t-2 border-ink">
          {spots.map((spot) => (
            <SpotRow
              key={spot.id}
              spot={spot}
              open={printAll || openId === spot.id}
              onToggle={() => setOpenId(openId === spot.id ? null : spot.id)}
            />
          ))}
        </div>
      </section>
    </>
  );
};

export default TacoIndex;
